import { useEffect } from "react";
import { Icon } from "@common/index";
import { PRODUCT_PLACEHOLDER_SRC } from "@utils/productImage";
import {
    optionElementId,
    type AutocompleteOption,
} from "./_helpers/autocompleteOptions";

interface AutocompleteProps {
    query: string;
    // The listbox's DOM id — the combobox in SearchInput points aria-controls at it.
    listboxId: string;
    // Already filtered, capped and ordered (categories first) by buildAutocompleteOptions; the
    // index into this list is the option's identity for the keyboard and for aria-activedescendant.
    options: AutocompleteOption[];
    // Index of the highlighted option, or -1 for none.
    activeIndex: number;
    // SearchPanel's verdict for the no-options case: true only once nothing is pending or
    // failed for products, so "No matches." never shows before a search has been attempted.
    // The Products status messages themselves live in SearchPanel's live region, OUTSIDE this
    // listbox — a message is not an option and cannot be an owned child of a listbox or group.
    showNoMatches: boolean;
    onSelect: (option: AutocompleteOption) => void;
}

// One listbox, two groups, options as the groups' direct children: the structure a combobox with
// aria-activedescendant needs, and the one the previous <ul role="listbox"> → <li> → <button
// role="option"> nesting was not. Options are deliberately NOT focusable — DOM focus stays on the
// input, so they also stay out of the panel's Tab trap.
export const Autocomplete = ({
    query,
    listboxId,
    options,
    activeIndex,
    showNoMatches,
    onSelect,
}: AutocompleteProps) => {
    // Keep the highlighted row in view: the list can be sixteen rows tall inside a panel that
    // scrolls, and an ArrowDown that moves aria-activedescendant off-screen is a keyboard the
    // sighted user cannot follow.
    useEffect(() => {
        if (activeIndex < 0) return;
        document
            .getElementById(optionElementId(activeIndex))
            ?.scrollIntoView({ block: "nearest" });
    }, [activeIndex]);

    if (options.length === 0) {
        if (!showNoMatches) return null;
        return (
            <div
                data-testid="autocomplete-empty"
                className="px-4 py-3 text-sm text-neutral/60"
            >
                No matches.
            </div>
        );
    }

    const categoryRows = options.flatMap((option, index) =>
        option.kind === "category" ? [{ category: option.category, index }] : [],
    );
    const productRows = options.flatMap((option, index) =>
        option.kind === "product" ? [{ product: option.product, index }] : [],
    );

    const rowClassName = (isActive: boolean) =>
        `w-full flex items-center gap-3 px-4 py-3 text-secondary cursor-pointer hover:bg-base-200 ${
            isActive ? "bg-base-200" : ""
        }`;

    return (
        <ul
            id={listboxId}
            data-testid="autocomplete"
            role="listbox"
            aria-label="Suggestions"
            className="divide-y divide-base-200"
        >
            {categoryRows.length > 0 && (
                <li role="group" aria-labelledby={`${listboxId}-categories`}>
                    <div
                        id={`${listboxId}-categories`}
                        role="presentation"
                        className="px-4 pt-3 pb-1 text-sm font-bold text-neutral"
                    >
                        Categories
                    </div>
                    {categoryRows.map(({ category, index }) => (
                        <div
                            key={category.id}
                            id={optionElementId(index)}
                            role="option"
                            aria-selected={index === activeIndex}
                            aria-label={category.displayName}
                            onClick={() =>
                                onSelect({ kind: "category", category })
                            }
                            className={rowClassName(index === activeIndex)}
                        >
                            <span className="flex-1 min-w-0 font-medium">
                                {renderHighlighted(category.displayName, query)}
                            </span>
                            <Icon
                                name="chevron_right"
                                className="text-xl text-secondary"
                            />
                        </div>
                    ))}
                </li>
            )}
            {productRows.length > 0 && (
                <li role="group" aria-labelledby={`${listboxId}-products`}>
                    <div
                        id={`${listboxId}-products`}
                        role="presentation"
                        className="px-4 pt-3 pb-1 text-sm font-bold text-neutral"
                    >
                        Products
                    </div>
                    {productRows.map(({ product, index }) => (
                        <div
                            key={product.id}
                            id={optionElementId(index)}
                            role="option"
                            aria-selected={index === activeIndex}
                            // Explicit, as for category rows: a name computed from the
                            // content would be split by the highlight markup
                            // ("Th ermal Mug"). Brand included — it is what tells two
                            // same-named products apart, and the row shows it for that.
                            aria-label={`${product.name}, ${product.brand}`}
                            onClick={() => onSelect({ kind: "product", product })}
                            className={rowClassName(index === activeIndex)}
                        >
                            {/* alt="" — the row's text is its label; a decorative
                                thumbnail must not be read out twice. */}
                            <img
                                src={product.thumbnailUrl ?? PRODUCT_PLACEHOLDER_SRC}
                                alt=""
                                className="w-10 h-10 shrink-0 rounded-md bg-base-100 object-contain"
                            />
                            <span className="flex-1 min-w-0 flex flex-col">
                                <span className="font-medium truncate">
                                    {renderHighlighted(product.name, query)}
                                </span>
                                <span className="text-xs text-neutral/60 truncate">
                                    {product.brand}
                                </span>
                            </span>
                            <Icon
                                name="chevron_right"
                                className="text-xl text-secondary"
                            />
                        </div>
                    ))}
                </li>
            )}
        </ul>
    );
};

const renderHighlighted = (displayName: string, query: string) => {
    const index = displayName.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return displayName;
    const before = displayName.slice(0, index);
    const match = displayName.slice(index, index + query.length);
    const after = displayName.slice(index + query.length);
    return (
        <>
            {before}
            <strong className="font-bold">{match}</strong>
            <span className="text-secondary/60">{after}</span>
        </>
    );
};
